import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for error tracking
 *
 * This should be called once on the client side only (in _app.tsx or layout.tsx)
 * Only initializes in production environment
 */
export function initSentry() {
  const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
  const appEnv = process.env.NEXT_PUBLIC_APP_ENV || 'local';
  const appVersion = process.env.NEXT_PUBLIC_APP_VERSION || 'dev';

  // Only initialize in production
  if (!dsn || appEnv !== 'production') {
    return;
  }

  // Skip if already initialized
  if (Sentry.getClient()) {
    return;
  }

  Sentry.init({
    dsn,
    environment: appEnv,
    release: appVersion,

    // Performance Monitoring: 10% sample rate
    tracesSampleRate: 0.1,

    // Session Replay: Only on errors
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration({
        enableInp: true,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Filter out common non-critical errors
    ignoreErrors: [
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      'NetworkError',
      'Network request failed',
      'AbortError',
      'The user aborted a request',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Minified React error #300',
      'Minified React error #301',
    ],
  });
}

/**
 * Set user context for Sentry error reports
 * Call this when user authenticates (from ClientAuthProvider)
 */
export function setSentryUser(
  user: {
    email?: string;
    username?: string;
  } | null
) {
  if (!Sentry.getClient()) {
    return;
  }

  if (!user) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: user.email,
    username: user.username,
    email: user.email,
  });
}

/**
 * Set site context for Sentry error reports
 * Call this when site changes (from ExplorerContext)
 */
export function setSentrySiteContext(
  site: {
    siteId?: string;
    siteName?: string;
  } | null
) {
  if (!Sentry.getClient()) {
    return;
  }

  if (!site) {
    Sentry.setTag('site_id', '');
    Sentry.setTag('site_name', '');
    return;
  }

  if (site.siteId) {
    Sentry.setTag('site_id', site.siteId);
  }
  if (site.siteName) {
    Sentry.setTag('site_name', site.siteName);
  }
}

/**
 * Capture an exception with optional context
 * Used by error boundaries to report React rendering errors
 */
export function captureException(
  error: Error,
  context?: {
    errorBoundary?: string;
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
  }
): string {
  if (!Sentry.getClient()) {
    return '';
  }

  return Sentry.captureException(error, {
    tags: context?.tags,
    extra: {
      errorBoundary: context?.errorBoundary,
      ...context?.extra,
    },
  });
}

/**
 * Capture API errors with structured context
 * Only call this for server errors (status >= 500).
 *
 * Do NOT call for expected failures like:
 * - 401 (unauthorized - expected for logged-out users)
 * - 403 (forbidden - expected permission denial)
 * - 404 (not found - expected for missing resources)
 */
export function captureApiError(
  error: Error,
  context: {
    endpoint: string;
    method?: string;
    statusCode?: number;
    requestBody?: unknown;
    responseBody?: unknown;
  }
): string {
  if (!Sentry.getClient()) {
    return '';
  }

  return Sentry.withScope((scope) => {
    scope.setTag('error_type', 'api_error');
    scope.setTag('endpoint', context.endpoint);
    scope.setTag('method', context.method || 'GET');

    if (context.statusCode) {
      scope.setTag('status_code', String(context.statusCode));
    }

    scope.setContext('api_request', {
      endpoint: context.endpoint,
      method: context.method,
      statusCode: context.statusCode,
      requestBody:
        typeof context.requestBody === 'string'
          ? context.requestBody.substring(0, 1000)
          : context.requestBody,
      responseBody:
        typeof context.responseBody === 'string'
          ? context.responseBody.substring(0, 1000)
          : context.responseBody,
    });

    return Sentry.captureException(error);
  });
}

/**
 * Capture SSE stream errors
 * PLACEHOLDER - NOT INTEGRATED
 *
 * This function is a placeholder for future use.
 * Currently not called anywhere in the codebase.
 * Will be integrated after observing streaming behavior in production.
 */
export function captureStreamError(
  error: Error,
  context: {
    sessionId?: string;
    eventType?: string;
    buffer?: string;
    endpoint?: string;
  }
): string {
  if (!Sentry.getClient()) {
    return '';
  }

  return Sentry.withScope((scope) => {
    scope.setTag('error_type', 'sse_stream_error');

    if (context.sessionId) {
      scope.setTag('session_id', context.sessionId);
    }
    if (context.eventType) {
      scope.setTag('event_type', context.eventType);
    }

    scope.setContext('stream_context', {
      sessionId: context.sessionId,
      eventType: context.eventType,
      endpoint: context.endpoint,
      buffer: context.buffer?.substring(0, 500),
    });

    return Sentry.captureException(error);
  });
}

/**
 * Add breadcrumb for debugging context
 */
export function addBreadcrumb(
  message: string,
  category?: string,
  data?: Record<string, unknown>
) {
  if (!Sentry.getClient()) {
    return;
  }

  Sentry.addBreadcrumb({
    message,
    category: category || 'app',
    data,
    level: 'info',
  });
}
